import React, { useEffect, useState, useRef } from "react";
import { useUserManagement } from "./hooks/useUserManagement";
import { useRoleManagement } from "./hooks/useRoleManagement";
import { FEATURES, FeatureGate } from "../../lib/utils/featureAccess";
import DataTable from "../../lib/components/DataTable";
import { Edit, Trash2, SlidersHorizontal, Plus } from "lucide-react";
import { Button, Box, Snackbar, Alert } from "@mui/material";
import { ROLE_OPTIONS, STATUS_OPTIONS } from "./constants";

const UserManagement = () => {
  const { users, loading, selectedUser, fetchUsers, createUser, updateUser, deleteUser, selectUser, clearError } =
    useUserManagement();
  const { roles } = useRoleManagement();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    organizationName: "",
    roleId: "READ",
    status: "ACTIVE",
  });
  const [showFilters, setShowFilters] = useState(false);
  const hasFetched = useRef(false);

  // Derive role options from backend roles, fallback to hardcoded
  const roleOptions =
    roles.length > 0 ? roles.map(r => ({ value: r.roleId, label: r.name || r.roleId })) : ROLE_OPTIONS;

  useEffect(() => {
    if (!hasFetched.current) {
      fetchUsers();
      hasFetched.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedUser) {
      setFormData({
        email: selectedUser.email || "",
        name: selectedUser.name || "",
        organizationName: selectedUser.organizationName || "",
        roleId: selectedUser.roleId || selectedUser.role || "READ",
        status: selectedUser.status || "ACTIVE",
      });
      setShowCreateModal(true);
    }
  }, [selectedUser]);

  const columns = [
    { key: "name", label: "Name", sortable: true },
    { key: "email", label: "Email", sortable: true },
    { key: "organizationName", label: "Organization", sortable: true },
    {
      key: "roleId",
      label: "Role",
      sortable: true,
      render: (value, row) => {
        // Use roleId if available, fallback to role (pre-migration users)
        const effectiveRole = value || row.role || "";
        const roleConfig = roles.find(r => r.roleId === effectiveRole);
        return <span>{roleConfig ? roleConfig.name : effectiveRole || "\u2014"}</span>;
      },
    },
    { key: "status", label: "Status", sortable: true },
    {
      key: "actions",
      label: "Actions",
      sortable: false,
      filterable: false,
      render: (value, row) => (
        <div className="flex gap-2">
          <FeatureGate featureId={FEATURES.USER_MANAGEMENT}>
            <button
              onClick={() => selectUser(row)}
              className="text-gray-600 hover:text-accent"
              aria-label={`Edit user ${row.email}`}
            >
              <Edit size={18} />
            </button>
            <button
              onClick={async () => {
                if (window.confirm(`Are you sure you want to delete ${row.email}?`)) {
                  try {
                    await deleteUser(row.email);
                  } catch {
                    // User deletion failed
                  }
                }
              }}
              className="text-red-600 hover:text-red-800"
              aria-label={`Delete user ${row.email}`}
            >
              <Trash2 size={18} />
            </button>
          </FeatureGate>
        </div>
      ),
    },
  ];

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      if (selectedUser) {
        await updateUser(selectedUser.email, formData);
        setToastMessage("User updated successfully");
        setShowToast(true);
        selectUser(null);
        setShowCreateModal(false);
      } else {
        await createUser(formData);
        setToastMessage("User created successfully");
        setShowToast(true);
        setShowCreateModal(false);
      }
      setFormData({ email: "", name: "", organizationName: "", roleId: "READ", status: "ACTIVE" });
      clearError();
    } catch {
      setToastMessage("Operation failed");
      setShowToast(true);
    }
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <FeatureGate featureId={FEATURES.USER_MANAGEMENT}>
      <div>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1.5, mb: 1.5 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setShowFilters(prev => !prev)}
            startIcon={<SlidersHorizontal size={15} />}
            sx={{
              textTransform: "none",
              borderColor: showFilters ? "var(--color-accent-active)" : "#d1d5db",
              borderRadius: "10px",
              color: showFilters ? "var(--color-accent-active)" : "#374151",
              backgroundColor: showFilters ? "var(--color-accent-alpha-06)" : "transparent",
              fontWeight: 600,
              fontSize: "0.8rem",
              px: 2,
              py: 0.7,
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: "var(--color-accent-active)",
                backgroundColor: "var(--color-accent-lightest)",
                color: "var(--color-accent-active)",
              },
            }}
          >
            {showFilters ? "Hide Filters" : "Show Filters"}
          </Button>
          <FeatureGate featureId={FEATURES.USER_MANAGEMENT}>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 btn-accent px-4 py-2 rounded"
            >
              <Plus size={16} />
              Add User
            </button>
          </FeatureGate>
        </Box>

        <DataTable columns={columns} data={users} loading={loading} showFilters={showFilters} pageSize={10} />

        {/* Create/Edit User Modal */}
        {(showCreateModal || selectedUser) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h2 className="text-xl font-bold mb-4">{selectedUser ? "Edit User" : "Create User"}</h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={!!selectedUser}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-accent"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    id="name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-accent"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="organizationName" className="block text-sm font-medium text-gray-700 mb-1">
                    Organization *
                  </label>
                  <input
                    id="organizationName"
                    type="text"
                    name="organizationName"
                    value={formData.organizationName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-accent"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="roleId" className="block text-sm font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <select
                    id="roleId"
                    name="roleId"
                    value={formData.roleId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-accent"
                  >
                    {roleOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-accent"
                  >
                    {STATUS_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      selectUser(null);
                      setFormData({ email: "", name: "", organizationName: "", roleId: "READ", status: "ACTIVE" });
                    }}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="px-4 py-2 btn-accent-submit rounded-md">
                    {selectedUser ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Toast Notification */}
        <Snackbar
          open={showToast}
          autoHideDuration={3000}
          onClose={() => setShowToast(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => setShowToast(false)}
            severity={toastMessage.includes("failed") ? "error" : "success"}
            sx={{ width: "100%" }}
          >
            {toastMessage}
          </Alert>
        </Snackbar>
      </div>
    </FeatureGate>
  );
};

export default UserManagement;
